/**
 * geoHelpers.ts
 * -------------------------------------------------------
 * 职责：GeoJSON 解析、省份数据加载、坐标工具等
 *       与 ECharts 配置无关的纯数据 / 网络逻辑。
 * -------------------------------------------------------
 */

import type {
    ProvinceCacheEntry,
    ProvinceGeoJson,
} from '../types';
import { resolveProvinceAdcodeLoose } from '../provinceAdcode';

/* -------- URL 构造 -------- */

/** 省级下钻：含地级市划分的 DataV `_full` */
export function provinceGeoUrlFull(adcode: string): string {
    return `/map/${adcode}_full.json`;
}

/** 单省整体边界（无市级要素时的回退） */
export function provinceGeoUrlSimple(adcode: string): string {
    return `/map/${adcode}.json`;
}

/* -------- 坐标遍历 -------- */

/**
 * 递归遍历 GeoJSON coordinates（任意深度嵌套），
 * 对每个 [lng, lat] 叶节点调用 visit。
 */
export function walkCoordinates(
    c: unknown,
    visit: (lng: number, lat: number) => void,
): void {
    if (!Array.isArray(c)) return;
    // 叶节点：[number, number, ...]
    if (c.length >= 2 && typeof c[0] === 'number' && typeof c[1] === 'number') {
        visit(c[0], c[1]);
        return;
    }
    for (const x of c) walkCoordinates(x, visit);
}

/* -------- 范围 & 中心计算 -------- */

export interface GeoSpanResult {
    spanDeg: number;
    centroid: [number, number];
    lngSpan: number;
    latSpan: number;
}

/** 海南省主岛近似外包框（与全省含南海诸岛数据解耦，仅作跨度兜底） */
const HAINAN_MAINLAND_LNG = [108.55, 111.35] as const;
const HAINAN_MAINLAND_LAT = [18.05, 20.25] as const;

function geoJsonSpanAndCentroidFromCityFeatures(
    geo: ProvinceGeoJson,
): GeoSpanResult | null {
    const cityFeatures = geo.features.filter((f) => f.properties?.level === 'city');
    if (cityFeatures.length === 0) return null;
    return geoJsonSpanAndCentroid({
        type: 'FeatureCollection',
        features: cityFeatures,
    });
}

/**
 * 以「最大面积要素」为种子，保留外包框中心距种子中心不超过阈值的要素再取并集。
 * 用于剔除远海小岛/示意多边形对全省外包框的拉伸（典型：海南）。
 */
function geoJsonSpanAndCentroidMainCluster(geo: ProvinceGeoJson): GeoSpanResult {
    if (geo.features.length <= 1) {
        return geoJsonSpanAndCentroid(geo);
    }

    const perFeature = geo.features.map((f) =>
        geoJsonSpanAndCentroid({ type: 'FeatureCollection', features: [f] }),
    );

    let seedIdx = 0;
    let seedArea = -1;
    perFeature.forEach((s, i) => {
        const a = s.lngSpan * s.latSpan;
        if (a > seedArea) {
            seedArea = a;
            seedIdx = i;
        }
    });

    const seed = perFeature[seedIdx];
    const thresh = Math.max(Math.max(seed.lngSpan, seed.latSpan) * 2.5, 3.5);

    const kept: ProvinceGeoJson['features'] = [];
    geo.features.forEach((f, i) => {
        const s = perFeature[i];
        const d = Math.hypot(
            s.centroid[0] - seed.centroid[0],
            s.centroid[1] - seed.centroid[1],
        );
        if (d <= thresh) kept.push(f);
    });

    if (kept.length === 0) return geoJsonSpanAndCentroid(geo);
    return geoJsonSpanAndCentroid({ type: 'FeatureCollection', features: kept });
}

/**
 * 省下钻用的外包框与跨度：在「全要素外包框」过宽时，优先用市级要素并集或主陆块聚类，
 * 避免远海/离岛把相机拉得过远（海南等）。
 * centroid 字段仅作占位，调用方应使用 provinceMapTargetCentroid。
 */
export function geoJsonSpanAndCentroidForProvinceDrill(
    adcode: string,
    geo: ProvinceGeoJson,
): GeoSpanResult {
    const full = geoJsonSpanAndCentroid(geo);
    const cluster = geoJsonSpanAndCentroidMainCluster(geo);
    const city = geoJsonSpanAndCentroidFromCityFeatures(geo);

    let best = full;
    const consider = (candidate: GeoSpanResult) => {
        if (
            candidate.spanDeg < full.spanDeg * 0.9 &&
            candidate.spanDeg >= full.spanDeg * 0.04 &&
            candidate.spanDeg < best.spanDeg
        ) {
            best = candidate;
        }
    };
    consider(cluster);
    if (city) consider(city);

    if (adcode === '460000' && best.spanDeg > 9) {
        const lngSpan = HAINAN_MAINLAND_LNG[1] - HAINAN_MAINLAND_LNG[0];
        const latSpan = HAINAN_MAINLAND_LAT[1] - HAINAN_MAINLAND_LAT[0];
        return {
            spanDeg: Math.max(lngSpan, latSpan, 0.35),
            lngSpan,
            latSpan,
            centroid: [
                (HAINAN_MAINLAND_LNG[0] + HAINAN_MAINLAND_LNG[1]) / 2,
                (HAINAN_MAINLAND_LAT[0] + HAINAN_MAINLAND_LAT[1]) / 2,
            ],
        };
    }

    return best;
}

/** 计算 GeoJSON 所有要素的外包矩形、中心点、跨度 */
export function geoJsonSpanAndCentroid(geo: ProvinceGeoJson): GeoSpanResult {
    let minLng = 180, maxLng = -180;
    let minLat = 90, maxLat = -90;

    for (const f of geo.features) {
        let featureMinLng = 180, featureMaxLng = -180;
        let featureMinLat = 90, featureMaxLat = -90;

        walkCoordinates(f.geometry.coordinates, (lng, lat) => {
            if (lng < featureMinLng) featureMinLng = lng;
            if (lng > featureMaxLng) featureMaxLng = lng;
            if (lat < featureMinLat) featureMinLat = lat;
            if (lat > featureMaxLat) featureMaxLat = lat;
        });

        if (featureMinLng < minLng) minLng = featureMinLng;
        if (featureMaxLng > maxLng) maxLng = featureMaxLng;
        if (featureMinLat < minLat) minLat = featureMinLat;
        if (featureMaxLat > maxLat) maxLat = featureMaxLat;
    }

    const lngSpan = Math.max(maxLng - minLng, 0.1);
    const latSpan = Math.max(maxLat - minLat, 0.1);

    return {
        spanDeg: Math.max(lngSpan, latSpan, 0.35),
        centroid: [(minLng + maxLng) / 2, (minLat + maxLat) / 2],
        lngSpan,
        latSpan,
    };
}

/* -------- adcode 读取 -------- */

/** 将可能为 string | number 的 adcode 统一为 6 位字符串 */
export function readSixDigitAdcode(v: unknown): string | null {
    if (typeof v === 'string' && v.length === 6) return v;
    if (typeof v === 'number') {
        const s = String(v);
        return s.length === 6 ? s : null;
    }
    return null;
}

/** 省级 / 市级要素 adcode → 制图中心点 */
export function buildAdcodeToCentroidMap(geo: ProvinceGeoJson): Map<string, [number, number]> {
    const m = new Map<string, [number, number]>();
    for (const f of geo.features) {
        const p = f.properties;
        if (!p) continue;
        const ad = readSixDigitAdcode(p.adcode);
        if (!ad) continue;
        const c = p.centroid ?? p.center;
        if (c && c.length >= 2) {
            m.set(ad, [Number(c[0]), Number(c[1])]);
        }
    }
    return m;
}

/* -------- 省份中心点 -------- */

/** 多要素省图（市级）时取省级中心；否则用外包矩形中心 */
export function provinceMapTargetCentroid(
    geo: ProvinceGeoJson,
    provinceAdcode: string,
): [number, number] {
    const ad = provinceAdcode.trim();

    // 优先：properties.level === 'province' 且 adcode 匹配
    for (const f of geo.features) {
        const p = f.properties;
        if (!p || p.level !== 'province') continue;
        if (readSixDigitAdcode(p.adcode) !== ad) continue;
        const c = p.centroid ?? p.center;
        if (c && c.length >= 2) return [Number(c[0]), Number(c[1])];
    }

    // 回退：任意 adcode 匹配的 feature
    for (const f of geo.features) {
        const p = f.properties;
        if (!p || readSixDigitAdcode(p.adcode) !== ad) continue;
        const c = p.centroid ?? p.center;
        if (c && c.length >= 2) return [Number(c[0]), Number(c[1])];
    }

    return geoJsonSpanAndCentroid(geo).centroid;
}

/* -------- 省份数据加载 -------- */

/**
 * 加载并缓存省份 GeoJSON，返回包含中心点、跨度等的完整缓存条目。
 * 先尝试 _full（含地级市），失败回退到简单版。
 */
export async function fetchProvinceCacheEntry(
    adcode: string,
    mapName: string,
): Promise<ProvinceCacheEntry> {
    let r = await fetch(provinceGeoUrlFull(adcode));
    if (!r.ok) r = await fetch(provinceGeoUrlSimple(adcode));
    if (!r.ok) throw new Error(String(r.status));

    const geoJson = (await r.json()) as ProvinceGeoJson;
    const { spanDeg, lngSpan, latSpan } = geoJsonSpanAndCentroidForProvinceDrill(
        adcode,
        geoJson,
    );
    const centroid = provinceMapTargetCentroid(geoJson, adcode);

    return { mapName, geoJson, spanDeg, centroid, lngSpan, latSpan };
}

/* -------- 省下钻距离 -------- */

export function distanceFromProvinceSpan(spanDeg: number): number {
    const d = 24 + spanDeg * 13;
    // PROVINCE_VIEW_DISTANCE_SCALE = 1.8（来自 mapConstants）
    return Math.min(Math.max(d, 30), 86) * 1.8;
}

/* -------- 点击参数解析 -------- */

/**
 * 将侧栏中的「市」字段与省下钻 GeoJSON 中的 `properties.name` 对齐。
 */
export function matchCityFeatureName(
    cityRaw: string,
    geo: ProvinceGeoJson,
): string | null {
    const raw = cityRaw.trim();
    if (!raw) return null;
    const candidates = new Set<string>([raw, `${raw}市`]);
    if (raw.endsWith('市')) {
        candidates.add(raw.slice(0, -1));
    }
    for (const f of geo.features) {
        const n = f.properties?.name?.trim();
        if (!n) continue;
        if (candidates.has(n)) return n;
    }
    for (const f of geo.features) {
        const n = f.properties?.name?.trim();
        if (!n) continue;
        if (n.includes(raw) || raw.includes(n.replace(/市$/, ''))) return n;
    }
    return null;
}

/** 按要素名取 centroid / center，用于地市视角居中 */
export function featureLngLatByName(
    geo: ProvinceGeoJson,
    featureName: string,
): [number, number] | null {
    const target = featureName.trim();
    if (!target) return null;
    for (const f of geo.features) {
        const n = f.properties?.name?.trim();
        if (n !== target) continue;
        const c = f.properties?.centroid ?? f.properties?.center;
        if (c && c.length >= 2) {
            return [Number(c[0]), Number(c[1])];
        }
        const { centroid } = geoJsonSpanAndCentroid({
            type: 'FeatureCollection',
            features: [f],
        } as ProvinceGeoJson);
        return centroid;
    }
    return null;
}

export function resolveProvinceAdcodeFromClickParams(
    params: import('../types').Geo3DClickParams,
): string | null {
    const { data, info, name } = params;

    // 1. data.adcode
    if (data && typeof data === 'object' && 'adcode' in data) {
        const ad = readSixDigitAdcode((data as Record<string, unknown>).adcode);
        if (ad) return ad;
    }

    // 2. info.adcode
    if (info && typeof info === 'object' && 'adcode' in info) {
        const ad = readSixDigitAdcode((info as Record<string, unknown>).adcode);
        if (ad) return ad;
    }

    // 3. data.name → 模糊匹配
    if (data && typeof data === 'object' && 'name' in data) {
        const n = (data as Record<string, unknown>).name;
        if (typeof n === 'string') {
            const ad = resolveProvinceAdcodeLoose(n);
            if (ad) return ad;
        }
    }

    // 4. params.name
    if (typeof name === 'string') {
        return resolveProvinceAdcodeLoose(name);
    }

    return null;
}

function normalizeAdminName(name: string): string {
    return name.trim().replace(/(特别行政区|自治区|省|市|地区|州|盟)$/g, '');
}

/** 点击落在散点附近时返回最近的散点（geo3D 经常把点击记到区块上） */
export function pickNearestScatterByLngLat<
    T extends { value: [number, number, number] },
>(items: T[], lng: number, lat: number, maxDeg: number): T | null {
    let best: T | null = null;
    let bestDist = maxDeg;
    for (const item of items) {
        const ilng = item.value[0];
        const ilat = item.value[1];
        if (!Number.isFinite(ilng) || !Number.isFinite(ilat)) continue;
        const dist = Math.hypot(ilng - lng, ilat - lat);
        if (dist < bestDist) {
            bestDist = dist;
            best = item;
        }
    }
    return best;
}

/**
 * 省下钻图点击地市区块时：若该市只有一个带 city 的散点，则选中它。
 * 解决 echarts-gl 把散点点击记到 mesh 上、射线拾取又失败的情况。
 */
export function pickUniqueScatterByCityName<
    T extends { city?: string },
>(items: T[], regionName: string): T | null {
    const regionKey = normalizeAdminName(regionName);
    if (!regionKey) return null;
    const matches = items.filter((item) => {
        const city = item.city?.trim();
        if (!city) return false;
        return (
            city === regionName.trim() ||
            normalizeAdminName(city) === regionKey
        );
    });
    return matches.length === 1 ? matches[0] : null;
}
